import { useAppDispatch } from '.';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
	setClientId,
	setPasses,
	setPerson,
	setReplacedPersonId,
	setUser,
} from '@/store/user-process/user-process';

import { useLoginMutation } from '@/api/authorization';
import { useLazyGetPersonByUserIdQuery } from '@/api/persons';

import { AutorizationData, AutorizationResponseType } from '@/types/authorization';

import { isAdmin } from '@/shared/helpers/isAdmin';
import { setAuthProvider } from '@/shared/helpers/keycloak';
import { useShowNotifaction } from '@/shared/hooks/useShowNotifaction.tsx';
import useUserStore from '@/shared/store/user.store.ts';
import { AutorizationResponseType as SessionResponse } from '@/shared/types/app.types';
import { IUser } from '@/shared/types/user.types.ts';

import usePassesCheckout from './usePassesCheckout';

const useLogin = () => {
	const navigate = useNavigate();
	const setNewUser = useUserStore((state) => state.setUser);
	const [isLoading, setLoading] = useState(false);
	const dispatch = useAppDispatch();

	const [doLogin] = useLoginMutation();
	const [getPersonByUsername] = useLazyGetPersonByUserIdQuery();

	const checkoutPass = usePassesCheckout();

	const completeLogin = useCallback(
		async (user: AutorizationResponseType, remember = true) => {
			const { setSession, setTargetPersonId, setIsAdmin } = useUserStore.getState();
			const session = user as unknown as SessionResponse;

			dispatch(setUser(user));
			dispatch(setClientId(user.user.person_id));
			dispatch(setReplacedPersonId(user.user.person_id));

			setSession(session);
			setTargetPersonId(user.user.person_id);
			setIsAdmin(Boolean(isAdmin(session)));

			if (remember) {
				localStorage.setItem('user', JSON.stringify(user));
			}

			const person = await getPersonByUsername(user.user.person_id).unwrap();
			setNewUser(person as unknown as IUser);
			dispatch(setPerson(person));

			await checkoutPass(remember, user);

			if (remember) {
				localStorage.setItem('person', JSON.stringify(person));
			}

			navigate('/', { replace: true });
		},
		[checkoutPass, dispatch, getPersonByUsername, navigate, setNewUser],
	);

	const login = useCallback(
		async (authData: AutorizationData) => {
			setLoading(true);

			const { remember, ...rest } = authData;
			try {
				const user = await doLogin({ ...rest, directory_id: 2 }).unwrap();
				setAuthProvider('password');
				await completeLogin(user, remember);
			} catch (err) {
				dispatch(setUser(null));
				dispatch(setPerson(null));
				dispatch(setClientId(null));
				dispatch(setReplacedPersonId(null));
				dispatch(setPasses(null));
				useUserStore.getState().reset();

				useShowNotifaction({ text: 'Не удалось выполнить вход', type: 'error' });
			} finally {
				setLoading(false);
			}
		},
		[completeLogin, dispatch, doLogin],
	);

	return { login, completeLogin, isLoading };
};

export default useLogin;
